import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { imgURL } from '../../../Config';

const getAssetUrl = (value, folder) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    const raw = value.trim();
    const baseUrl = String(imgURL || '/storage/').replace(/\/+$/, '');
    let url;

    if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) {
        url = new URL(raw, window.location.origin).href;
    } else {
        const path = raw.replace(/^\.\//, '').replace(/^\/+/, '').replace(/^storage\//, '');
        const storagePath = path.startsWith('publications/')
            ? path
            : /^(files|thumbnails)\//.test(path)
                ? 'publications/' + path
                : 'publications/' + folder + '/' + path;
        url = baseUrl + '/' + storagePath;
    }

    // Use the existing storage proxy when running the local development server.
    if (process.env.NODE_ENV === 'development'
        && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
        && url.startsWith(baseUrl + '/')) {
        return window.location.origin + '/storage/' + url.slice(baseUrl.length + 1);
    }

    return url;
};

export default function PublicationAssetActions({ value, folder, label }) {
    const [downloading, setDownloading] = useState(false);
    const [localAsset, setLocalAsset] = useState(null);

    useEffect(() => {
        if (!(value instanceof File)) {
            setLocalAsset(null);
            return;
        }

        const objectUrl = URL.createObjectURL(value);
        setLocalAsset({ file: value, url: objectUrl });
        return () => URL.revokeObjectURL(objectUrl);
    }, [value]);

    const url = value instanceof File
        ? (localAsset?.file === value ? localAsset.url : '')
        : getAssetUrl(value, folder);

    if (!url) return null;

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');

            const blobUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = value instanceof File
                ? value.name
                : url.split('/').pop().split('?')[0].split('#')[0] || label;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
        } catch (error) {
            Swal.fire({
                title: 'Download failed',
                text: 'Unable to download this ' + label.toLowerCase() + '. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="publication-asset-actions">
            <Button
                as="a"
                variant="outline-primary"
                size="sm"
                className="publication-asset-action"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={'View ' + label.toLowerCase() + ' in a new tab'}
                aria-label={'View ' + label.toLowerCase() + ' in a new tab'}
            >
                <i className="bi bi-eye" aria-hidden="true"></i>
            </Button>
            <Button
                type="button"
                variant="outline-success"
                size="sm"
                className="publication-asset-action"
                onClick={handleDownload}
                disabled={downloading}
                title={'Download ' + label.toLowerCase()}
                aria-label={(downloading ? 'Downloading ' : 'Download ') + label.toLowerCase()}
                aria-busy={downloading}
            >
                {downloading
                    ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    : <i className="bi bi-download" aria-hidden="true"></i>}
            </Button>
        </div>
    );
}
